/**
 * `/websocket/chat` — the socket adapter of the same conversation layer.
 * Events: `ack{client_message_id}` → `typing` → `message | degraded | error`.
 * The store owns the state; this class only moves frames and reconnects.
 */

import { apiClient } from '@/api/api'
import {
  transformKeysToCamelCase,
  transformKeysToSnakeCase,
} from '@/api/utils/caseTransformer'
import type {
  AiraAction,
  AiraIntent,
  AiraLatency,
  AiraMessageStatus,
  AiraSendMessageBody,
  AiraSocketErrorCode,
  AiraSocketEvent,
} from './types'

export type AiraSocketStatus = 'connecting' | 'open' | 'closed' | 'failed'

export interface AiraSocketStatusChange {
  status: AiraSocketStatus
  /** The connection came back after a drop — the thread needs a re-read. */
  reconnected: boolean
}

const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_DELAY_MS = 1000

const SOCKET_EVENT_TYPES = new Set(['ack', 'typing', 'message', 'degraded', 'error'])

/** `ws(s)://<api root>/websocket/chat?token=Bearer …` — the backend reads the token from the query. */
export function airaSocketUrl(token: string | null, baseUrl = apiClient.defaults.baseURL ?? '/api/v0/'): string | null {
  if (typeof window === 'undefined') return null
  const absolute = /^https?:\/\//.test(baseUrl) ? baseUrl : `${window.location.origin}${baseUrl}`
  const url = new URL(absolute)
  url.pathname = `${url.pathname.replace(/\/$/, '')}/websocket/chat`
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  if (token) url.searchParams.set('token', `Bearer ${token}`)
  return url.toString()
}

/** A raw frame → a typed event; anything unknown is dropped, not thrown. */
export function parseAiraSocketEvent(raw: unknown): AiraSocketEvent | null {
  let data: unknown = raw
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (typeof data !== 'object' || data === null) return null
  const event = transformKeysToCamelCase<Record<string, unknown>>(data)
  const type = event.type
  if (typeof type !== 'string' || !SOCKET_EVENT_TYPES.has(type)) return null
  const clientMessageId = typeof event.clientMessageId === 'string' ? event.clientMessageId : undefined
  if (type === 'error') {
    const code = typeof event.code === 'string' ? (event.code as AiraSocketErrorCode) : 'internal'
    return {
      type: 'error',
      code,
      clientMessageId,
      retryAfter: typeof event.retryAfter === 'number' ? event.retryAfter : undefined,
      detail: typeof event.detail === 'string' ? event.detail : undefined,
    }
  }
  if (!clientMessageId) return null
  if (type === 'ack' || type === 'typing') {
    return { type, clientMessageId }
  }
  const latency = (event.latencyMs ?? {}) as Partial<AiraLatency>
  return {
    type: type as 'message' | 'degraded',
    clientMessageId,
    messageId: typeof event.messageId === 'number' ? event.messageId : null,
    threadId: typeof event.threadId === 'number' ? event.threadId : null,
    content: typeof event.content === 'string' ? event.content : '',
    status: type === 'degraded' ? 'degraded' : ((event.status as AiraMessageStatus | undefined) ?? 'ok'),
    retryable: event.retryable === true,
    intent: (event.intent as AiraIntent | undefined) ?? 'answer',
    actions: Array.isArray(event.actions) ? (event.actions as AiraAction[]) : [],
    latencyMs: {
      total: latency.total ?? 0,
      ml: latency.ml ?? 0,
      phases: latency.phases ?? {},
      profileCache: latency.profileCache ?? 'none',
    },
  }
}

export class AiraChatSocket {
  private ws: WebSocket | null = null
  private attempts = 0
  private closedByClient = false
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private everOpened = false
  private readonly eventListeners = new Set<(event: AiraSocketEvent) => void>()
  private readonly statusListeners = new Set<(change: AiraSocketStatusChange) => void>()

  constructor(private readonly resolveUrl: () => string | null) {}

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  onEvent(listener: (event: AiraSocketEvent) => void): () => void {
    this.eventListeners.add(listener)
    return () => this.eventListeners.delete(listener)
  }

  onStatus(listener: (change: AiraSocketStatusChange) => void): () => void {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  connect(): Promise<void> {
    if (this.isOpen) return Promise.resolve()
    const url = this.resolveUrl()
    if (!url || typeof WebSocket === 'undefined') return Promise.reject(new Error('aira socket unavailable'))
    this.closedByClient = false
    return new Promise((resolve, reject) => {
      let settled = false
      this.notifyStatus('connecting', false)
      let socket: WebSocket
      try {
        socket = new WebSocket(url)
      } catch (error) {
        this.notifyStatus('failed', false)
        reject(error)
        return
      }
      this.ws = socket
      socket.onopen = () => {
        settled = true
        const reconnected = this.everOpened
        this.everOpened = true
        this.attempts = 0
        this.notifyStatus('open', reconnected)
        resolve()
      }
      socket.onmessage = (event: MessageEvent) => {
        const parsed = parseAiraSocketEvent(event.data)
        if (parsed) this.eventListeners.forEach((listener) => listener(parsed))
      }
      socket.onerror = () => {
        if (!settled) {
          settled = true
          reject(new Error('aira socket failed to connect'))
        }
      }
      socket.onclose = () => {
        if (this.ws === socket) this.ws = null
        if (this.closedByClient) {
          this.notifyStatus('closed', false)
          return
        }
        if (!settled) {
          settled = true
          reject(new Error('aira socket closed before opening'))
        }
        this.scheduleReconnect()
      }
    })
  }

  /** True when the frame left over the socket; false means "use REST". */
  send(body: AiraSendMessageBody, language: string): boolean {
    if (!this.isOpen || !this.ws) return false
    const frame = transformKeysToSnakeCase({
      type: 'message',
      content: body.text,
      language,
      clientMessageId: body.clientMessageId,
      context: body.context,
    })
    this.ws.send(JSON.stringify(frame))
    return true
  }

  close(): void {
    this.closedByClient = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.attempts = 0
    const socket = this.ws
    this.ws = null
    socket?.close()
  }

  private scheduleReconnect(): void {
    if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
      this.notifyStatus('failed', false)
      return
    }
    const delay = RECONNECT_BASE_DELAY_MS * 2 ** this.attempts
    this.attempts += 1
    this.notifyStatus('connecting', false)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect().catch(() => undefined)
    }, delay)
  }

  private notifyStatus(status: AiraSocketStatus, reconnected: boolean): void {
    this.statusListeners.forEach((listener) => listener({ status, reconnected }))
  }
}
